import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ENV } from '../app/apiScreenSlice';
import { PARAM_IN_STORE } from '../assets/consts/inStoreConsts';
import { getEnv } from '../utils/environments';
import { isSDK } from '../utils/sdk';

export const initDatadog = () => {
  const [appEnv] = getEnv(window.location.hostname.toLowerCase());
  const env = appEnv === ENV.LOCAL ? 'development' : appEnv;
  const clientToken = 'pub399abfdc8f67a5cfd5601326d6c4c24f';
  const site = 'datadoghq.com';
  const service = 'web-application';

  datadogRum.init({
    applicationId: 'ccbde738-9718-4c42-b510-d759f3ae4770',
    clientToken,
    site,
    service,
    env,
    // TODO: version
    sessionSampleRate: env === 'development' ? 0 : 100,
    sessionReplaySampleRate: env === 'development' ? 0 : 100,
    defaultPrivacyLevel: 'mask-user-input',
    trackUserInteractions: true,
    plugins: [reactPlugin],
    allowedTracingUrls: [
      'https://api.imprint.co',
      'https://int.stg.imprintapi.co',
      'https://sbx.imprint.co',
      'https://int.preprd.imprintapi.co',
      'https://app.stg.imprint.co',
    ],
    usePartitionedCrossSiteSessionCookie: true, //! this value must match datadogLogs config
  });

  datadogLogs.init({
    clientToken,
    site,
    service,
    env,
    // TODO: version
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    usePartitionedCrossSiteSessionCookie: true, //! this value must match datadogRum config
  });
};

export const DatadogRouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    datadogRum.startView(location.pathname);
  }, [location.pathname]);

  return null;
};

export const useDatadogRum = ({
  consumerUUID,
  productUUID,
  merchantKey,
  sessionID,
  productInitialized,
}: {
  consumerUUID: string;
  productUUID: string;
  merchantKey: string;
  sessionID: string;
  productInitialized: boolean;
}) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (consumerUUID) {
      datadogRum.setUserProperty('id', consumerUUID);
    }
  }, [consumerUUID]);

  useEffect(() => {
    if (productUUID) {
      datadogRum.setGlobalContextProperty('product_uuid', productUUID);
    }
  }, [productUUID]);

  useEffect(() => {
    if (merchantKey) {
      datadogRum.setGlobalContextProperty('merchant_key', merchantKey);
    }
  }, [merchantKey]);

  useEffect(() => {
    if (sessionID) {
      datadogRum.setGlobalContextProperty('session_id', sessionID);
    }
  }, [sessionID]);

  useEffect(() => {
    /*
      To determine the mode, we read the value 'isSDK' from sessionStorage. This value is
      written to sessionStorage in the 'initProductFromUrl' function in App.tsx. We need 
      to make sure this value is set prior to reading it to determine the mode.
    */
    if (!productInitialized) return;

    let mode = 'direct';

    if (isSDK()) {
      mode = 'embedded_sdk';
    } else if (searchParams.get(PARAM_IN_STORE) === 'true') {
      mode = 'in_store';
    }

    datadogRum.setGlobalContextProperty('mode', mode);
  }, [location, productInitialized, searchParams]);
};

export const useDatadogLogs = ({
  consumerUUID,
  productUUID,
  merchantKey,
  sessionID,
  productInitialized,
}: {
  consumerUUID: string;
  productUUID: string;
  merchantKey: string;
  sessionID: string;
  productInitialized: boolean;
}) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (consumerUUID) {
      datadogLogs.setGlobalContextProperty('consumer_uuid', consumerUUID);
    }
  }, [consumerUUID]);

  useEffect(() => {
    if (productUUID) {
      datadogLogs.setGlobalContextProperty('product_uuid', productUUID);
    }
  }, [productUUID]);

  useEffect(() => {
    if (merchantKey) {
      datadogLogs.setGlobalContextProperty('merchant_key', merchantKey);
    }
  }, [merchantKey]);

  useEffect(() => {
    if (sessionID) {
      datadogLogs.setGlobalContextProperty('session_id', sessionID);
    }
  }, [sessionID]);

  useEffect(() => {
    /*
      To determine the mode, we read the value 'isSDK' from sessionStorage. This value is
      written to sessionStorage in the 'initProductFromUrl' function in App.tsx. We need 
      to make sure this value is set prior to reading it to determine the mode.
    */
    if (!productInitialized) return;

    let mode = 'direct';

    if (isSDK()) {
      mode = 'embedded_sdk';
    } else if (searchParams.get(PARAM_IN_STORE) === 'true') {
      mode = 'in_store';
    }

    datadogLogs.setGlobalContextProperty('mode', mode);
  }, [location, productInitialized, searchParams]);
};
